import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { getHttpClient, useHttpClient } from '@bonusly/http-client';
import { getPermission, Permission, useIdentity } from '@bonusly/who-am-i';

import { getQueryClient } from '../lib/query-client';
import { CompanyItem, CompanyItemDetail, CoreCategoryKey, DistributionMethod, Provider, Reward } from '../schemas/rewards';
import { listOf } from '../schemas/utils';
import { toSnakeCaseKeys } from '../utils/object';
import { headers, Metadata, PaginatedData, PaginationParams } from './meta';

const QueryKey = {
	CompanyItem: 'rewards:company-item',
	RewardRedemptions: 'rewards:reward-redemptions',
	ItemCategories: 'rewards:item-categories',
	ItemSubcategories: 'rewards:item-subcategories',
	ItemSearch: 'rewards:item-search',
	Filters: 'rewards:filters',
	Rewards: 'rewards:rewards',
	CatalogSettings: 'rewards:catalog-settings',
};

export const companyItemsQueryKey = (params?: Record<string, any>) => [QueryKey.CompanyItem, params].filter(Boolean);
export const companyItemQueryKey = (id: string) => [QueryKey.CompanyItem, id];
export const rewardRedemptionsQueryKey = (params?: RewardRedemptionAPIParams) => [QueryKey.RewardRedemptions, params].filter(Boolean);
export const rewardRedemptionQueryKey = (id: string) => [QueryKey.RewardRedemptions, id];
export const itemCategoriesQueryKey = () => [QueryKey.ItemCategories];
export const itemSubcategoriesQueryKey = () => [QueryKey.ItemSubcategories];
export const itemSearchQueryKey = (search: string) => [QueryKey.ItemSearch, search];
export const filtersQueryKey = () => [QueryKey.Filters];
export const rewardsQueryKey = (params?: RewardsAPIParams) => [QueryKey.Rewards, params].filter(Boolean);
export const settingsQueryKey = () => [QueryKey.CatalogSettings];

// This one is for creating a redemption in the top level controller
export const redemptionsPath = (prefix = '') => `${prefix}/redemptions`;
// This one follows newer patterns and is part of the rewards namespace
export const rewardRedemptionsPath = (prefix = '') => `${prefix}/rewards/redemptions`;
export const rewardRedemptionPath = (id: string, prefix = '') => `${prefix}/rewards/redemptions/${id}`;
export const resendGiftEmailPath = (id: string, prefix = '') => `${prefix}/rewards/redemptions/${id}/resend`;
export const itemCategoriesPath = (prefix = '') => `${prefix}/company_item_categories`;
export const itemSubcategoriesPath = (prefix = '') => `${prefix}/catalogs/swag_sub_categories`;
export const itemsPath = (prefix = '') => `${prefix}/catalogs/gridwall`;
export const featuredPath = (prefix = '') => `${prefix}/catalogs/featured`;
export const filtersPath = (prefix = '') => `${prefix}/rewards/filters`;
export const rewardsPath = (prefix = '') => `${prefix}/rewards`;
export const settingsPath = (prefix = '') => `${prefix}/admin/settings`;

// Yes, these both have the same path, but one goes to RCS while the other to Recognize...
export const companyItemPath = (id: string, prefix = '') => `${prefix}/company_items/${id}`;
export const itemStatusPath = (id: string, prefix = '') => `${prefix}/company_items/${id}`;

export type RedemptionAPIData = {
	redemption: {
		id: string;
	};
};

export type CreateRedemptionAPIData = {
	color?: string;
	denominationId?: string;
	fulfillmentPartner?: string;
	giftEmail?: string;
	giftFirstName?: string;
	giftMessage?: string;
	isGift: boolean;
	shippingAddressAttributes?: {
		address1?: string;
		address2?: string;
		city?: string;
		state?: string;
		country?: string;
		postalCode?: string;
		phoneNumber?: string;
	};
	size?: string;
};

type MutateRedemptionsOptions = {
	onError?: (error: unknown) => void;
	onMutate?: (data: CreateRedemptionAPIData) => void;
	onSuccess?: (celebration: RedemptionAPIData) => void;
};

export const useClaimReward = (options: MutateRedemptionsOptions = {}) => {
	const http = useHttpClient();
	const queryClient = useQueryClient();
	const invalidateRedemptions = useInvalidateRedemptions();
	const { refetchProfile } = useIdentity();

	return useMutation({
		mutationFn: async (data: CreateRedemptionAPIData) => {
			try {
				options.onMutate?.(data);
				const res = await http.post<{ result: RedemptionAPIData }>(redemptionsPath(), data, { headers });
				options.onSuccess?.(res.data.result);

				// Note: This should be updated to import invalidation hooks rather than directly
				// invalidating the queries. The keys are not stable.
				queryClient.invalidateQueries(['recognition-user']);
				queryClient.invalidateQueries(['rewards-user']);
				queryClient.invalidateQueries(['navbar-profile']);

				refetchProfile();
				invalidateRedemptions();

				return res.data.result;
			} catch (e) {
				options.onError?.(e);
				throw e;
			}
		},
	});
};

export const useCompanyItem = (id: string = '') => {
	const rcs = useHttpClient('rcs');

	return useQuery({
		queryKey: companyItemQueryKey(id),
		queryFn: async () => {
			// The API data coming back is pretty gnarly so I'm just going to let CompanyItem parse out what is
			// actually needed and used
			const itemRes = await rcs.get(companyItemPath(id), { params: { view: 'item_detail' } });
			return CompanyItemDetail.parse(itemRes.data.data);
		},
		enabled: !!id,
		keepPreviousData: false,
	});
};

export type CompanyItemsAPIParams = {
	category?: string;
	focusCurated?: boolean;
	itemType?: string;
	subCategory?: string;
	pageNumber: number;
};

type CompanyItemsAPIData = {
	data: any;
	meta: {
		pagination: {
			pageCount: number;
			pageNext: number | null;
			pagePrevious: number | null;
			pageNumber: number;
			pageSize: number;
			totalCount: number;
		};
	};
};

const createCompanyItemsFetcher = (params: CompanyItemsAPIParams) => async () => {
	const http = await getHttpClient('rcs');

	// I don't like this. There are two endpoints to return data for company items and the previous UI
	// has different components and queries to show what are essentially the same things. Instead
	// this just massages the data to make it consistent.
	if (params.category === 'featured') {
		// The only time the points boost will ever be visible is in the Points Boost
		// category, the whole thing being hidden if the user doesn't have permission.
		// Or, in the Featured category, which has this separate API request.
		//
		// That being said, I found that this doesn't even work in the back end so we need
		// to rework some stuff anyway. Looks like the current feature has a bug.
		const canClaimPointsBoost = await getPermission(Permission.CanClaimPointsBoost);

		const formattedParams: any = {
			pagination: {
				enabled: false,
				page_size: 9,
			},
			view: 'gridwall',
		};

		if (!canClaimPointsBoost) {
			formattedParams.exclude = { points_boost: true };
		}

		const res = (await http.get<CompanyItemsAPIData>(featuredPath(), { params: formattedParams, headers })).data;

		return [listOf(CompanyItem).parse(res.data), new Metadata()] as const;
	}

	const formattedParams: any = {
		pagination: {
			enabled: true,
			page_size: 24,
			page_number: params.pageNumber,
		},
		filters: {},
		view: 'gridwall',
	};

	if (params.category) {
		formattedParams.filters.category_ids = [params.category];
	}

	if (params.itemType) {
		formattedParams.filters.item_type = params.itemType;
	}

	if (params.focusCurated) {
		formattedParams.filters.provider = 'snappy';
	}

	if (params.subCategory) {
		formattedParams.filters.sub_category = params.subCategory;
	}

	const res = (await http.get<CompanyItemsAPIData>(itemsPath(), { params: formattedParams, headers })).data;

	const meta = new Metadata({
		...res.meta.pagination,
		totalPages: res.meta.pagination.pageCount,
	});

	return [listOf(CompanyItem).parse(res.data), meta] as const;
};

export const loadCompanyItems = async (params: CompanyItemsAPIParams) => {
	const queryClient = await getQueryClient();
	return queryClient.fetchQuery({
		queryKey: companyItemsQueryKey(params),
		queryFn: createCompanyItemsFetcher(params),
	});
};

export const useCompanyItems = (params: CompanyItemsAPIParams) => {
	return useQuery({
		queryKey: companyItemsQueryKey(params),
		queryFn: createCompanyItemsFetcher(params),
	});
};

export type RewardRedemptionAPIData = {
	id: string;
	aasmState: 'new' | 'approved' | 'fulfilled' | 'declined' | 'refunded';
	brandName: string;
	claimUrl: string | null;
	color: string | null;
	createdAt: string;
	donation: boolean;
	gift: boolean;
	giftCard: boolean;
	gifteeEmail: string | null;
	name: string;
	priceInCurrency: number;
	priceInPoints: number;
	redemptionInstructions: string | null;
	size: string | null;
	catalogItem: {
		id: string;
		denomination: {
			id: string;
			optionName: string;
			optionType: string;
			quantity: number | null;
			distributionMethod: DistributionMethod;
			provider: Provider;
		} | null;
		imageUrl: string;
		type: 'axomo' | 'built_in' | 'custom' | 'other';
	};
	redemptionRecords: {
		id: string;
		amount: number;
		createdAt: string;
		state: 'new' | 'manual_pending' | 'manual_fulfilled' | 'succeeded' | 'failed' | 'canceled' | 'retry' | 'out_of_stock' | 'pending_voucher' | 'expired';
		transactionFee: number | null;
		errorMessage: string | null;
		pin: string | null;
		code: string | null;
		number: string | null;
		orderId: string | null;
		redemptionUrl: string | null;
		barcodeImageUrl: string | null;
		certificateUrl: string | null;
		expirationDate: string | null;
	}[];
};

export type RewardRedemptionAPIParams = PaginationParams;

const createRewardRedemptionsFetcher = (params?: RewardRedemptionAPIParams) => async () => {
	const http = await getHttpClient();
	const res = await http.get<PaginatedData<RewardRedemptionAPIData>>(rewardRedemptionsPath(), { headers, params });

	return [res.data.records, new Metadata(res.data.metadata)] as const;
};

export const loadRewardRedemptions = async (params?: RewardRedemptionAPIParams) => {
	const queryClient = await getQueryClient();
	return queryClient.fetchQuery({
		queryKey: rewardRedemptionsQueryKey(params),
		queryFn: createRewardRedemptionsFetcher(params),
	});
};

export const useRewardRedemptions = (params?: RewardRedemptionAPIParams) => {
	return useQuery({
		queryKey: rewardRedemptionsQueryKey(params),
		queryFn: createRewardRedemptionsFetcher(params),
	});
};

export type RewardRedemptionDetailAPIData = RewardRedemptionAPIData & {
	shippingAddress: {
		address1: string;
		address2: string;
		city: string;
		state: string;
		postalCode: string;
		country: string;
	} | null;
};

const createRewardRedemptionFetcher = (id: string) => async () => {
	const http = await getHttpClient();
	return (await http.get<RewardRedemptionDetailAPIData>(rewardRedemptionPath(id), { headers })).data;
};

export const loadRewardRedemption = async (id: string) => {
	const queryClient = await getQueryClient();
	return queryClient.fetchQuery({
		queryKey: rewardRedemptionQueryKey(id),
		queryFn: createRewardRedemptionFetcher(id),
	});
};

export const useRewardRedemption = (id?: string) => {
	return useQuery({
		queryKey: rewardRedemptionQueryKey(id as string),
		queryFn: createRewardRedemptionFetcher(id as string),
		enabled: !!id,
		keepPreviousData: false,
	});
};

export const useInvalidateRedemptions = () => {
	const queryClient = useQueryClient();
	return () => {
		queryClient.invalidateQueries(rewardRedemptionsQueryKey());
	};
};

export const useInvalidateRedemption = () => {
	const queryClient = useQueryClient();
	return (id: string) => {
		queryClient.invalidateQueries(rewardRedemptionQueryKey(id));
	};
};

type MutateGiftReceipintOptions = {
	onError?: (error: unknown) => void;
	onSuccess?: (celebration: RewardRedemptionAPIData) => void;
};

export const useUpdateGiftRecipient = (options: MutateGiftReceipintOptions = {}) => {
	const http = useHttpClient();
	const invalidate = useInvalidateRedemption();

	return useMutation({
		mutationFn: async ({ id, email }: { id: string; email: string }) => {
			try {
				const res = await http.post<RewardRedemptionAPIData>(resendGiftEmailPath(id), { email }, { headers });
				options.onSuccess?.(res.data);
				invalidate(id);
				return res.data;
			} catch (e) {
				options.onError?.(e);
				throw e;
			}
		},
	});
};

type ItemSubcategoriesAPIData = {
	data: string[];
};

const createItemSubcategoriesFetcher = () => async () => {
	const http = await getHttpClient('rcs');
	const res = await http.get<ItemSubcategoriesAPIData>(itemSubcategoriesPath(), { headers });

	return res.data.data;
};

export const loadItemSubcategories = async () => {
	const queryClient = await getQueryClient();
	return queryClient.fetchQuery({
		queryKey: itemSubcategoriesQueryKey(),
		queryFn: createItemSubcategoriesFetcher(),
	});
};

export const useItemSubcategories = () => {
	return useQuery({
		queryKey: itemSubcategoriesQueryKey(),
		queryFn: createItemSubcategoriesFetcher(),
	});
};

// Item Categories

export type ItemCategoriesAPIData = {
	data: {
		id: string;
		key: CoreCategoryKey;
		name: string;
	}[];
	meta: {
		exclusiveItems: boolean;
		featuredItems: boolean;
		swagItems: boolean;
		curatedItems: boolean;
	};
};

const createItemCategoriesFetcher = () => async () => {
	const http = await getHttpClient('rcs');
	const canClaimPointsBoost = await getPermission(Permission.CanClaimPointsBoost);

	let params: Record<string, any> | undefined = undefined;

	if (!canClaimPointsBoost) {
		params = {
			exclude: { points_boost: true },
		};
	}

	return (await http.get<ItemCategoriesAPIData>(itemCategoriesPath(), { headers, params })).data;
};

export const loadItemCategories = async () => {
	const queryClient = await getQueryClient();
	return queryClient.fetchQuery({
		queryKey: itemCategoriesQueryKey(),
		queryFn: createItemCategoriesFetcher(),
	});
};

export const useItemCategories = () => {
	return useQuery({
		queryKey: itemCategoriesQueryKey(),
		queryFn: createItemCategoriesFetcher(),
	});
};

// Items search

export type ItemSearchAPIData = {
	id: string;
	item: {
		brandName: string;
		distributionMethod: DistributionMethod;
		fullName: string;
		id: string;
		logo: {
			id: string;
			altText: string;
			imageUrl: string;
		};
	};
};

export const useSearchItems = (search: string) => {
	const http = useHttpClient('rcs');

	return useQuery({
		queryKey: itemSearchQueryKey(search),
		queryFn: async () => {
			return (
				await http.get<{ data: ItemSearchAPIData[] }>(itemsPath(), {
					params: {
						filters: {
							name: search,
						},
						pagination: {
							page_size: 10,
							page_number: 1,
						},
						view: 'search_results',
					},
					headers,
				})
			).data.data;
		},
		enabled: !!search,
	});
};

// Updated rewards index handlers

export type FitlersAPIData = {
	collections: {
		key: 'featured' | 'exclusive' | 'swag' | 'all';
		id: string | null;
		name: string | null;
	}[];
	filters: {
		key: 'category' | 'price' | 'type';
		options: { key: string }[];
	}[];
};

const createFiltersFetcher = () => async () => {
	const http = await getHttpClient('rcs');
	return (await http.get<FitlersAPIData>(filtersPath(), { headers })).data;
};

export const loadFilters = async () => {
	const queryClient = await getQueryClient();
	return queryClient.fetchQuery({
		queryKey: filtersQueryKey(),
		queryFn: createFiltersFetcher(),
	});
};

export const useFilters = () => {
	return useQuery({
		queryKey: filtersQueryKey(),
		queryFn: createFiltersFetcher(),
	});
};

type RewardsAPIParams = {
	q?: string | null;
	filters?: {
		collection?: string;
		category?: string[];
		price?: string[];
		type?: string[];
	};
	pageNumber?: number;
};

export type RewardsAPIData = PaginatedData<{
	id: string;
	name: string;
	minAmountInPoints: number;
	maxAmountInPoints: number;
	labels: string[];
}>;

const createRewardsFetcher =
	(params: RewardsAPIParams = {}) =>
	async () => {
		const http = await getHttpClient('rcs');

		// Add pageSize and transform keys to snake_case
		const apiParams = toSnakeCaseKeys({
			...params,
			pageSize: 24,
		});

		const res = (await http.get<RewardsAPIData>(rewardsPath(), { headers, params: apiParams })).data;

		return [listOf(Reward).parse(res.records), new Metadata(res.metadata)] as const;
	};

export const loadRewards = async (params: RewardsAPIParams = {}) => {
	const queryClient = await getQueryClient();
	return queryClient.fetchQuery({
		queryKey: rewardsQueryKey(params),
		queryFn: createRewardsFetcher(params),
	});
};

export const useRewards = (params: RewardsAPIParams = {}) => {
	return useQuery({
		queryKey: rewardsQueryKey(params),
		queryFn: createRewardsFetcher(params),
	});
};

// Admin queries

type CatalogSettingsAPIData = {
	giftCardsEnabled: boolean;
	donationsEnabled: boolean;
	curatedGiftsEnabled: boolean;
	autoAddNewRewards: boolean;
	maxPriceInUsdCents: number | null;
};

const createSettingsFetcher = () => async () => {
	const http = await getHttpClient('rcs');
	return (await http.get<CatalogSettingsAPIData>(settingsPath(), { headers })).data;
};

export const loadCatalogSettings = async () => {
	const queryClient = await getQueryClient();
	return queryClient.fetchQuery({
		queryKey: settingsQueryKey(),
		queryFn: createSettingsFetcher(),
	});
};

export const useCatalogSettings = () => {
	return useQuery({
		queryKey: settingsQueryKey(),
		queryFn: createSettingsFetcher(),
	});
};

export const useStoreCatalogSettings = () => {
	const queryClient = useQueryClient();

	return useCallback(
		(record: CatalogSettingsAPIData) => {
			queryClient.setQueryData<CatalogSettingsAPIData>(settingsQueryKey(), record);
		},
		[queryClient]
	);
};

export type MutateSettingsOptions = {
	onError?: (error: unknown) => void;
	onSuccess?: (record: CatalogSettingsAPIData) => void;
};

export const useUpdateCatalogSettings = (options: MutateSettingsOptions = {}) => {
	const http = useHttpClient('rcs');
	const store = useStoreCatalogSettings();

	return useMutation({
		mutationFn: async (params: CatalogSettingsAPIData) => {
			try {
				const res = (await http.patch<CatalogSettingsAPIData>(settingsPath(), params, { headers })).data;
				store(res);
				options.onSuccess?.(res);
			} catch (e) {
				options.onError?.(e);
				throw e;
			}
		},
	});
};
